import React, {useState, useEffect} from "react";
import {AlertCircle, ArrowLeft, ToggleLeft, ToggleRight, Plus, Users} from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {useToast} from "@/components/ui/use-toast";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import SearchFilter from "./SearchFilter";
import DashboardContainer from "../layout/DashboardContainer";
import {useParams, useNavigate} from "react-router-dom";
import ApiService from "@/services/api";
import {EndpointKeys} from "@/config/api";

interface Feature {
    key: string;
    description: string;
    is_enabled: boolean;
    updated_at: string;
    last_updated_by: string | null;
}

interface CreateFeatureFlagForm {
    key: string;
    description: string;
    is_enabled: boolean;
}

const FeatureList: React.FC = () => {
    const [features, setFeatures] = useState<Feature[]>([]);
    const [filteredFeatures, setFilteredFeatures] = useState<Feature[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const {toast} = useToast();
    const navigate = useNavigate();
    
    const form = useForm<CreateFeatureFlagForm>({
        defaultValues: {
            key: "",
            description: "",
            is_enabled: false,
        },
    });
    const handleBackClick = () => {
        navigate("/operations/dashboard");
    };
    useEffect(() => {
        // Fetch features from API
        const fetchFeatures = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await ApiService.request<Feature[]>(
                    EndpointKeys.GET_FEATURE_FLAGS,
                    {
                        method: "GET"
                    }
                );
                setFeatures(response);
                setFilteredFeatures(response);
            } catch (err) {
                setError("Failed to load features. Please try again later.");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFeatures();
    }, []);

    // Apply all filters
    useEffect(() => {
        let filtered = [...features];

        // Apply search filter
        if (searchQuery.trim()) {
            const lowercaseQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (feature) =>
                    feature.key.toLowerCase().includes(lowercaseQuery) ||
                    feature.description.toLowerCase().includes(lowercaseQuery)
            );
        }

        setFilteredFeatures(filtered);
    }, [features, searchQuery]);

    // Handle search
    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    // Get status badge with appropriate styling
    const getStatusBadge = (isActive: boolean) => {
        return isActive ? (
            <Badge className="bg-green-100 text-green-800 flex items-center">
                <ToggleRight className="w-3 h-3 mr-1"/>
                Active
            </Badge>
        ) : (
            <Badge variant="outline" className="flex items-center">
                <ToggleLeft className="w-3 h-3 mr-1"/>
                Inactive
            </Badge>
        );
    };

    const handleToggleFeature = async (featureKey: string, currentStatus: boolean) => {
        try {
            const response = await ApiService.request(
                EndpointKeys.TOGGLE_FEATURE_FLAG,
                {
                    method: "PATCH",
                    urlParams: {key: featureKey}
                }
            );

            if (response) {
                // Update the local state with the new status
                setFeatures(prevFeatures =>
                    prevFeatures.map(feature =>
                        feature.key === featureKey
                            ? {...feature, is_enabled: !currentStatus}
                            : feature
                    )
                );
                setFilteredFeatures(prevFeatures =>
                    prevFeatures.map(feature =>
                        feature.key === featureKey
                            ? {...feature, is_enabled: !currentStatus}
                            : feature
                    )
                );

                toast({
                    title: "Success",
                    description: `Feature ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update feature status. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handleCreateFeatureFlag = async (data: CreateFeatureFlagForm) => {
        setIsCreating(true);
        try {
            const response = await ApiService.request<Feature>(
                EndpointKeys.CREATE_FEATURE_FLAG,
                {
                    method: "POST",
                    body: data
                }
            );

            if (response) {
                // Add the new feature to the list
                setFeatures(prevFeatures => [...prevFeatures, response]);
                setFilteredFeatures(prevFeatures => [...prevFeatures, response]);
                
                // Reset form and close dialog
                form.reset();
                setIsCreateDialogOpen(false);

                toast({
                    title: "Success",
                    description: "Feature flag created successfully",
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to create feature flag. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsCreating(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <p className="text-gray-500">Loading features...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
                    <p className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2"/>
                        {error}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <DashboardContainer
            title={`Features Dashboard`}
            headerContent={
                <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center"
                    onClick={handleBackClick}
                >
                    <ArrowLeft className="w-4 h-4 mr-1"/>
                    Back to Dashboard
                </Button>
            }
        >
            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <CardTitle>Feature Management</CardTitle>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <SearchFilter
                                onSearch={handleSearch}
                                placeholder="Search features..."
                            />
                            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Create Feature Flag
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[425px]">
                                    <DialogHeader>
                                        <DialogTitle>Create New Feature Flag</DialogTitle>
                                        <DialogDescription>
                                            Create a new feature flag to control functionality across the application.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <Form {...form}>
                                        <form onSubmit={form.handleSubmit(handleCreateFeatureFlag)} className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="key"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Feature Key</FormLabel>
                                                        <FormControl>
                                                            <Input 
                                                                placeholder="e.g., new_dashboard_feature" 
                                                                {...field} 
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Unique identifier for the feature flag (use snake_case)
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="description"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Description</FormLabel>
                                                        <FormControl>
                                                            <Textarea 
                                                                placeholder="Describe what this feature flag controls..."
                                                                {...field} 
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="is_enabled"
                                                render={({ field }) => (
                                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                        <div className="space-y-0.5">
                                                            <FormLabel className="text-base">
                                                                Enable by default
                                                            </FormLabel>
                                                            <FormDescription>
                                                                Whether this feature should be enabled when created
                                                            </FormDescription>
                                                        </div>
                                                        <FormControl>
                                                            <Switch
                                                                checked={field.value}
                                                                onCheckedChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <DialogFooter>
                                                <Button 
                                                    type="button" 
                                                    variant="outline" 
                                                    onClick={() => setIsCreateDialogOpen(false)}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button type="submit" disabled={isCreating}>
                                                    {isCreating ? "Creating..." : "Create Feature Flag"}
                                                </Button>
                                            </DialogFooter>
                                        </form>
                                    </Form>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {filteredFeatures.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {isLoading ? (
                                <p>Loading features...</p>
                            ) : error ? (
                                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
                                    <p className="flex items-center justify-center">
                                        <AlertCircle className="w-5 h-5 mr-2"/>
                                        {error}
                                    </p>
                                </div>
                            ) : (
                                <p>No features found</p>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Feature Key</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Last Updated</TableHead>
                                    <TableHead>Actions</TableHead>
                                    <TableHead>User Overrides</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredFeatures.map((feature) => (
                                    <TableRow key={feature.key}>
                                        <TableCell className="font-medium">{feature.key}</TableCell>
                                        <TableCell>{feature.description}</TableCell>
                                        <TableCell>{getStatusBadge(feature.is_enabled)}</TableCell>
                                        <TableCell>{new Date(feature.updated_at).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleToggleFeature(feature.key, feature.is_enabled)}
                                                className="flex items-center"
                                            >
                                                {feature.is_enabled ? (
                                                    <>
                                                        <ToggleRight className="w-4 h-4 mr-1"/>
                                                        Deactivate
                                                    </>
                                                ) : (
                                                    <>
                                                        <ToggleLeft className="w-4 h-4 mr-1"/>
                                                        Activate
                                                    </>
                                                )}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    // Navigate to user management page for this feature
                                                    navigate(`/operations/features/${feature.key}/users`);
                                                }}
                                                className="flex items-center"
                                            >
                                                <Users className="w-4 h-4 mr-1"/>
                                                Manage Users
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </DashboardContainer>
    );
};

export default FeatureList;
